import type {DefaultError} from "@tanstack/react-query";
import {useMutation} from "@tanstack/react-query";
import type {UrlQueryKey} from "~/query/queryClient.ts";
import {queryClient, useCustomQuery} from "~/query/queryClient.ts";
import {queryHttp} from "~/query/queryHttp.ts";
import {contractsUrl} from "./useContractExplorerQueries.ts";

export type SpendMixRow = {
    name: string;
    /** material | service for golden-record rows; null for categories. */
    type: 'material' | 'service' | null;
    spend: number;
    share: number;
    transactions: number;
};

export type InsightPeriod = {effective: string | null; expiration: string | null};

export type SpendMixResponse = {
    available: boolean;
    period: InsightPeriod;
    total_spend: number;
    materials: SpendMixRow[];
    categories: SpendMixRow[];
};

export type CoverageRow = {
    name: string;
    spend: number;
    in_contract: number;
    other_contract: number;
    off_contract: number;
    contracted_share: number;
};

export type CoverageResponse = {
    available: boolean;
    reason?: string;
    supplier_name?: string;
    match_level?: string;
    /** Present when available: the contract period the buckets were computed for. */
    period?: InsightPeriod;
    supplier_spend: number;
    in_contract: number;
    other_contract: number;
    off_contract: number;
    entities: CoverageRow[];
    materials: CoverageRow[];
};

export type LeakageSupplier = {
    supplier_id: number;
    name: string;
    leakage: number;
    supplier_spend: number;
    share_of_supplier_spend: number;
    materials: {name: string; spend: number; transactions: number}[];
};

export type LeakageResponse = {
    available: boolean;
    period: InsightPeriod;
    total: number;
    suppliers: LeakageSupplier[];
};

export type LeakageTransaction = {
    p_id: string;
    date: string | null;
    entity: string;
    description: string;
    spend: number;
};

const insightsUrl = (bagId: number, contractId: number, insight: string) =>
    `${contractsUrl(bagId)}${contractId}/insights/${insight}/`;

// All keys sit under the contract's URL prefix, so the matching mutations'
// invalidation (which sweeps that prefix) refreshes insights after
// accept/reject/claim changed what counts as contracted spend.

export function useSpendMixQuery(bagId: number, contractId: number, enabled: boolean) {
    return useCustomQuery<SpendMixResponse, DefaultError, SpendMixResponse, UrlQueryKey>({
        queryKey: [insightsUrl(bagId, contractId, 'spend-mix')],
        enabled: enabled && bagId > 0 && contractId > 0,
        staleTime: 30_000,
    });
}

export function useCoverageInsightQuery(bagId: number, contractId: number, enabled: boolean) {
    return useCustomQuery<CoverageResponse, DefaultError, CoverageResponse, UrlQueryKey>({
        queryKey: [insightsUrl(bagId, contractId, 'coverage')],
        enabled: enabled && bagId > 0 && contractId > 0,
        staleTime: 30_000,
    });
}

/** One entity × item cell of the off-contract matrix; null when the cell has
 *  no off-contract spend. */
export type OffContractCell = {
    off: number;
    total: number;
    off_share: number;
    transactions: number;
} | null;

export type OffContractResponse = {
    available: boolean;
    reason?: string;
    supplier_name?: string;
    match_level?: string;
    period?: InsightPeriod;
    total_off: number;
    entities: string[];
    items: {name: string; off_total: number; cells: OffContractCell[]}[];
};

export function useOffContractMatrixQuery(bagId: number, contractId: number, enabled: boolean) {
    return useCustomQuery<OffContractResponse, DefaultError, OffContractResponse, UrlQueryKey>({
        queryKey: [insightsUrl(bagId, contractId, 'off-contract')],
        enabled: enabled && bagId > 0 && contractId > 0,
        staleTime: 30_000,
    });
}

/** How maverick spend defines "similar to what the contract covers":
 *  same golden-record cluster (item) or same leaf category (category). */
export type LeakageMode = 'item' | 'category';

export function useLeakageQuery(bagId: number, contractId: number, enabled: boolean, mode: LeakageMode) {
    return useCustomQuery<LeakageResponse, DefaultError, LeakageResponse, UrlQueryKey>({
        queryKey: [insightsUrl(bagId, contractId, 'leakage'), {params: {mode}}],
        enabled: enabled && bagId > 0 && contractId > 0,
        staleTime: 30_000,
    });
}

/** The drill-down: one supplier + group cell's underlying transactions
 *  (`material` carries the group label — a harmonized name in item mode,
 *  a leaf category in category mode). */
export function useLeakageTransactionsQuery(
    bagId: number, contractId: number, supplierId: number | null, material: string | null,
    mode: LeakageMode,
) {
    return useCustomQuery<{transactions: LeakageTransaction[]}, DefaultError,
        {transactions: LeakageTransaction[]}, UrlQueryKey>({
        queryKey: [insightsUrl(bagId, contractId, 'leakage'),
            {params: {supplier_id: supplierId, material, mode}}],
        enabled: bagId > 0 && contractId > 0 && supplierId !== null && material !== null,
        staleTime: 30_000,
    });
}

// ---- the AI contract report ------------------------------------------------

export type ContractReportResponse = {
    available: boolean;
    generating: boolean;
    stale?: boolean;
    generated_at?: string;
    model?: string;
    report_markdown?: string;
};

const reportUrl = (bagId: number, contractId: number) =>
    insightsUrl(bagId, contractId, 'report');

export function useContractReportQuery(bagId: number, contractId: number, enabled: boolean) {
    return useCustomQuery<ContractReportResponse, DefaultError, ContractReportResponse, UrlQueryKey>({
        queryKey: [reportUrl(bagId, contractId)],
        enabled: enabled && bagId > 0 && contractId > 0,
        staleTime: 30_000,
    });
}

/**
 * Generate (or regenerate) the stored report. Runs the LLM inline on the
 * backend — seconds to a minute, the run-matching shape — hence the raised
 * timeout. The response is the fresh GET payload, so it lands straight in
 * the query cache.
 */
export function useGenerateReportMutation() {
    return useMutation<ContractReportResponse, DefaultError, {bagId: number; contractId: number}>({
        mutationFn: async ({bagId, contractId}) => {
            const {data} = await queryHttp.post<ContractReportResponse>(
                reportUrl(bagId, contractId), {}, {timeout: 5 * 60 * 1000},
            );
            return data;
        },
        onSuccess: (data, {bagId, contractId}) => {
            queryClient.setQueryData([reportUrl(bagId, contractId)], data);
        },
    });
}
